// AnnouncementsPage.tsx - Manage household announcements
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { IonIcon, IonFab, IonFabButton, IonRefresher, IonRefresherContent, IonAlert, RefresherEventDetail } from '@ionic/react';
import { add, trash, pin, megaphone, timeOutline, documentTextOutline, saveOutline, eyeOutline, chevronDown, chevronUp } from 'ionicons/icons';
import { format, parseISO, isPast, isFuture } from 'date-fns';
import { supabase } from '../services/supabase';
import {
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  subscribeToAnnouncements,
  getAnnouncementTemplates,
  createAnnouncementTemplate,
  type CreateAnnouncementData,
  type AnnouncementTemplate,
} from '../services/announcements';
import type { Announcement, AnnouncementPriority } from '../types';
import { useHouseholdId } from '../contexts/AuthContext';

// UI Library imports
import { PageShell } from '../components/ui/PageShell';
import { Button } from '../components/ui/Button';
import { Modal, ModalSection } from '../components/ui/Modal';
import { Search } from '../components/ui/Search';
import { Segment, type SegmentOption } from '../components/ui/Segment';
import { Badge } from '../components/ui/Badge';
import { TextInput } from '../components/ui/TextInput';
import { Card } from '../components/ui/Card';
import { List, type ListItemProps } from '../components/ui/List';
import { IconPickerModal, ClickableIcon, type IconValue } from '../components/ui/IconPicker';
import { ColorPicker } from '../components/ui/ColorPicker';

import './AnnouncementsPage.css';

type ViewTab = 'active' | 'scheduled' | 'expired' | 'all';

// View tab options for Segment
const VIEW_TAB_OPTIONS: SegmentOption<ViewTab>[] = [
  { value: 'active', label: 'Active' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'expired', label: 'Expired' },
  { value: 'all', label: 'All' },
];

// Priority options
const PRIORITY_OPTIONS = [
  { id: 'low', value: 'low', label: 'Low' },
  { id: 'normal', value: 'normal', label: 'Normal' },
  { id: 'high', value: 'high', label: 'High' },
  { id: 'urgent', value: 'urgent', label: 'Urgent' },
];

// Font size options
const FONT_SIZE_OPTIONS = [
  { id: 'small', value: 'small', label: 'Small' },
  { id: 'medium', value: 'medium', label: 'Medium' },
  { id: 'large', value: 'large', label: 'Large' },
  { id: 'xlarge', value: 'xlarge', label: 'Extra Large' },
];

// Default form values
const DEFAULT_FORM = {
  title: '',
  message: '',
  icon: { type: 'emoji' as const, name: '📢' } as IconValue,
  priority: 'normal' as AnnouncementPriority,
  is_sticky: false,
  show_on_dashboard: true,
  show_on_screensaver: false,
  starts_at: '',
  expires_at: '',
  color: '',
  font_size: 'medium',
};

type AnnouncementFormData = typeof DEFAULT_FORM;

const AnnouncementsPage: React.FC = () => {
  const householdId = useHouseholdId();
  const location = useLocation();
  const history = useHistory();

  const [activeTab, setActiveTab] = useState<ViewTab>('active');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showIconPickerModal, setShowIconPickerModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState<AnnouncementFormData>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteAlert, setDeleteAlert] = useState<{ isOpen: boolean; id: string | null }>({
    isOpen: false,
    id: null,
  });

  // Template states
  const [templates, setTemplates] = useState<AnnouncementTemplate[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Preview state for scheduled announcements
  const [showPreview, setShowPreview] = useState(false);

  // Fetch all announcements (not just active)
  const fetchAnnouncements = useCallback(async () => {
    if (!householdId) return;

    const { data, error } = await supabase
      .from('announcements')
      .select(`
        *,
        creator:family_members!created_by(id, name, nickname, avatar_emoji, avatar_url, color)
      `)
      .eq('household_id', householdId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching announcements:', error);
      return;
    }

    setAnnouncements(data || []);
    setLoading(false);
  }, [householdId]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    if (!householdId) return;
    const data = await getAnnouncementTemplates(householdId);
    setTemplates(data);
  }, [householdId]);

  // Load data on mount
  useEffect(() => {
    fetchAnnouncements();
    fetchTemplates();

    // Subscribe to real-time updates
    if (householdId) {
      const unsubscribe = subscribeToAnnouncements(householdId, fetchAnnouncements);
      return unsubscribe;
    }
  }, [householdId, fetchAnnouncements, fetchTemplates]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await fetchAnnouncements();
    event.detail.complete();
  };

  // Get announcement status
  const getStatus = (announcement: Announcement): 'active' | 'scheduled' | 'expired' => {
    const now = new Date();
    const startsAt = parseISO(announcement.starts_at);
    const expiresAt = announcement.expires_at ? parseISO(announcement.expires_at) : null;

    if (isFuture(startsAt)) return 'scheduled';
    if (expiresAt && isPast(expiresAt)) return 'expired';
    return 'active';
  };

  // Filter announcements by tab and search
  const filteredAnnouncements = announcements.filter(a => {
    // Tab filter
    if (activeTab !== 'all') {
      const status = getStatus(a);
      if (status !== activeTab) return false;
    }

    // Search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      return (
        a.title.toLowerCase().includes(search) ||
        a.message?.toLowerCase().includes(search)
      );
    }

    return true;
  });

  // Helper to convert stored icon string to IconValue
  const parseIconValue = (iconStr: string | null | undefined): IconValue => {
    if (!iconStr) return { type: 'emoji', name: '📢' };
    // If it's already a simple emoji, wrap it
    return { type: 'emoji', name: iconStr };
  };

  // Helper to serialize IconValue for storage
  const serializeIcon = (iconValue: IconValue): string => {
    // For emoji type, just return the emoji character
    if (iconValue.type === 'emoji') return iconValue.name;
    // For other types, store as JSON
    return JSON.stringify(iconValue);
  };

  // Open form modal
  const openFormModal = (announcement?: Announcement) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setFormData({
        title: announcement.title,
        message: announcement.message || '',
        icon: parseIconValue(announcement.icon),
        priority: announcement.priority,
        is_sticky: announcement.is_sticky,
        show_on_dashboard: announcement.show_on_dashboard,
        show_on_screensaver: announcement.show_on_screensaver,
        starts_at: announcement.starts_at ? format(parseISO(announcement.starts_at), "yyyy-MM-dd'T'HH:mm") : '',
        expires_at: announcement.expires_at ? format(parseISO(announcement.expires_at), "yyyy-MM-dd'T'HH:mm") : '',
        color: announcement.color || '',
        font_size: (announcement as any).font_size || 'medium',
      });
    } else {
      setEditingAnnouncement(null);
      setFormData({
        ...DEFAULT_FORM,
        starts_at: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      });
    }
    setShowFormModal(true);
  };

  // Close form modal
  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingAnnouncement(null);
    setFormData(DEFAULT_FORM);
  };

  // Handle ?action=add query parameter to auto-open form modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'add') {
      openFormModal();
      // Clear the query param so it doesn't reopen on navigation
      history.replace('/settings/announcements');
    }
  }, [location.search, history]);

  // Save announcement
  const handleSave = async () => {
    if (!formData.title.trim() || !householdId) return;

    setSaving(true);

    const data: CreateAnnouncementData = {
      household_id: householdId,
      title: formData.title.trim(),
      message: formData.message.trim() || undefined,
      icon: serializeIcon(formData.icon),
      priority: formData.priority,
      is_sticky: formData.is_sticky,
      show_on_dashboard: formData.show_on_dashboard,
      show_on_screensaver: formData.show_on_screensaver,
      starts_at: formData.starts_at ? new Date(formData.starts_at).toISOString() : undefined,
      expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : undefined,
      color: formData.color || undefined,
      // Note: created_by requires a family_member_id, not auth user id
      // Leaving undefined for now - could be enhanced to look up linked family member
    };

    let result;
    if (editingAnnouncement) {
      result = await updateAnnouncement(editingAnnouncement.id, data);
    } else {
      result = await createAnnouncement(data);
    }

    setSaving(false);

    if (result) {
      closeFormModal();
      await fetchAnnouncements();
    }
  };

  // Delete announcement
  const handleDelete = async () => {
    if (!deleteAlert.id) return;

    const success = await deleteAnnouncement(deleteAlert.id);
    if (success) {
      setAnnouncements(prev => prev.filter(a => a.id !== deleteAlert.id));
    }
    setDeleteAlert({ isOpen: false, id: null });
  };

  // Apply template to form
  const applyTemplate = (template: AnnouncementTemplate) => {
    setFormData({
      title: template.title_template,
      message: template.message_template || '',
      icon: parseIconValue(template.icon),
      priority: template.priority,
      is_sticky: template.is_sticky,
      show_on_dashboard: template.show_on_dashboard,
      show_on_screensaver: template.show_on_screensaver,
      starts_at: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      expires_at: template.default_duration_hours
        ? format(new Date(Date.now() + template.default_duration_hours * 60 * 60 * 1000), "yyyy-MM-dd'T'HH:mm")
        : '',
      color: template.color || '',
      font_size: 'medium',
    });
    setShowTemplates(false);
  };

  // Save current form as template
  const handleSaveAsTemplate = async () => {
    if (!templateName.trim() || !householdId) return;

    setSavingTemplate(true);

    const result = await createAnnouncementTemplate({
      household_id: householdId,
      name: templateName.trim(),
      description: templateDescription.trim() || undefined,
      icon: serializeIcon(formData.icon),
      title_template: formData.title,
      message_template: formData.message || undefined,
      priority: formData.priority,
      color: formData.color || undefined,
      is_sticky: formData.is_sticky,
      show_on_dashboard: formData.show_on_dashboard,
      show_on_screensaver: formData.show_on_screensaver,
      default_duration_hours: formData.expires_at && formData.starts_at
        ? Math.round((new Date(formData.expires_at).getTime() - new Date(formData.starts_at).getTime()) / (1000 * 60 * 60))
        : undefined,
      // Note: created_by requires a family_member_id, not auth user id
    });

    setSavingTemplate(false);

    if (result) {
      setShowSaveTemplateModal(false);
      setTemplateName('');
      setTemplateDescription('');
      await fetchTemplates();
    }
  };

  // Check if announcement is scheduled (starts in future)
  const isScheduled = formData.starts_at && isFuture(new Date(formData.starts_at));

  // Get priority badge
  const getPriorityBadge = (priority: AnnouncementPriority) => {
    const variants: Record<AnnouncementPriority, 'neutral' | 'primary' | 'warning' | 'danger'> = {
      low: 'neutral',
      normal: 'primary',
      high: 'warning',
      urgent: 'danger',
    };
    return <Badge variant={variants[priority]} size="sm">{priority}</Badge>;
  };

  // Get status badge
  const getStatusBadge = (announcement: Announcement) => {
    const status = getStatus(announcement);
    const variants: Record<string, 'success' | 'warning' | 'neutral'> = {
      active: 'success',
      scheduled: 'warning',
      expired: 'neutral',
    };
    return <Badge variant={variants[status]} size="sm">{status}</Badge>;
  };

  // Convert announcements to List items
  const announcementItems: ListItemProps[] = filteredAnnouncements.map(announcement => ({
    id: announcement.id,
    label: announcement.title,
    description: announcement.message || undefined,
    onClick: () => openFormModal(announcement),
    start: {
      element: (
        <span style={{ fontSize: 24 }}>{announcement.icon || '📢'}</span>
      ),
    },
    end: {
      element: (
        <Button
          fill="clear"
          size="small"
          iconStart={trash}
          onClick={(e) => {
            e.stopPropagation();
            setDeleteAlert({ isOpen: true, id: announcement.id });
          }}
          aria-label="Delete announcement"
          style={{ color: 'var(--ion-color-danger)' }}
        />
      ),
    },
    meta: (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {getStatusBadge(announcement)}
        {getPriorityBadge(announcement.priority)}
        {announcement.is_sticky && (
          <Badge variant="primary" size="sm">
            <IonIcon icon={pin} style={{ marginRight: 4, fontSize: 12 }} />
            Pinned
          </Badge>
        )}
        {announcement.expires_at && (
          <Badge variant="neutral" fill="outline" size="sm">
            <IonIcon icon={timeOutline} style={{ marginRight: 4, fontSize: 12 }} />
            Expires {format(parseISO(announcement.expires_at), 'MMM d')}
          </Badge>
        )}
        <span style={{
          fontSize: 11,
          color: 'var(--ion-color-medium)',
          fontFamily: 'var(--kff-data-font)',
        }}>
          {announcement.creator?.nickname || announcement.creator?.name || 'Unknown'}
        </span>
      </div>
    ),
  }));

  // Count by status
  const counts = {
    active: announcements.filter(a => getStatus(a) === 'active').length,
    scheduled: announcements.filter(a => getStatus(a) === 'scheduled').length,
    expired: announcements.filter(a => getStatus(a) === 'expired').length,
    all: announcements.length,
  };

  return (
    <PageShell
      title="Announcements"
      headerType="page"
      showBack
      renderBeforeContent={
        <div style={{ padding: '0 16px 12px' }}>
          <Segment
            options={VIEW_TAB_OPTIONS.map(opt => ({
              ...opt,
              label: `${opt.label} (${counts[opt.value]})`,
            }))}
            value={activeTab}
            onChange={setActiveTab}
            size="sm"
          />
        </div>
      }
    >
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      {/* Search */}
      <div style={{ padding: '0 16px 16px' }}>
        <Search
          value={searchText}
          onChange={setSearchText}
          placeholder="Search announcements..."
          variant="filled"
          size="md"
        />
      </div>

      {/* Announcements List */}
      {loading ? (
        <Card style={{ margin: 16, textAlign: 'center' }}>
          <div style={{ padding: '24px 16px' }}>
            <p style={{ fontFamily: 'var(--kff-data-font)', color: 'var(--ion-color-medium)' }}>
              Loading...
            </p>
          </div>
        </Card>
      ) : announcementItems.length === 0 ? (
        <Card style={{ margin: 16, textAlign: 'center' }}>
          <div style={{ padding: '24px 16px' }}>
            <IonIcon icon={megaphone} style={{ fontSize: 48, color: 'var(--ion-color-medium)', opacity: 0.5 }} />
            <p style={{
              fontFamily: 'var(--kff-data-font)',
              color: 'var(--ion-color-medium)',
              marginTop: 16,
              marginBottom: 8,
            }}>
              {searchText ? 'No announcements match your search' : `No ${activeTab === 'all' ? '' : activeTab} announcements`}
            </p>
            {!searchText && activeTab === 'active' && (
              <Button
                variant="primary"
                iconStart={add}
                onClick={() => openFormModal()}
                style={{ marginTop: 16 }}
              >
                Create Announcement
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <List
          items={announcementItems}
          variant="default"
          size="md"
          lines="inset"
        />
      )}

      {/* FAB */}
      <IonFab vertical="bottom" horizontal="end" slot="fixed">
        <IonFabButton onClick={() => openFormModal()}>
          <IonIcon icon={add} />
        </IonFabButton>
      </IonFab>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showFormModal}
        onClose={closeFormModal}
        size="lg"
        header={{ title: editingAnnouncement ? 'Edit Announcement' : 'New Announcement' }}
        footer={{
          primaryAction: {
            label: saving ? 'Saving...' : (editingAnnouncement ? 'Save Changes' : 'Create'),
            onClick: handleSave,
            disabled: !formData.title.trim() || saving,
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: closeFormModal,
          },
        }}
      >
        <ModalSection spacing="normal">
          {/* Template Picker (only for new announcements) */}
          {!editingAnnouncement && templates.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <button
                type="button"
                onClick={() => setShowTemplates(!showTemplates)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '12px 16px',
                  background: 'var(--ion-color-step-50)',
                  border: '1px solid var(--ion-border-color)',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontFamily: 'var(--kff-data-font)',
                  fontSize: 14,
                  color: 'var(--ion-text-color)',
                }}
              >
                <IonIcon icon={documentTextOutline} style={{ fontSize: 20, color: 'var(--ion-color-primary)' }} />
                <span style={{ flex: 1, textAlign: 'left' }}>Use a Template</span>
                <IonIcon icon={showTemplates ? chevronUp : chevronDown} style={{ fontSize: 18 }} />
              </button>

              {showTemplates && (
                <div style={{
                  marginTop: 8,
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: '1px solid var(--ion-border-color)',
                  borderRadius: 10,
                  background: 'var(--ion-card-background)',
                }}>
                  {templates.map(template => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => applyTemplate(template)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '1px solid var(--ion-border-color)',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ fontSize: 24 }}>{template.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontFamily: 'var(--kff-heading-font)',
                          fontSize: 14,
                          fontWeight: 600,
                          color: 'var(--ion-text-color)',
                        }}>
                          {template.name}
                          {template.is_system && (
                            <Badge variant="neutral" size="sm" style={{ marginLeft: 8 }}>System</Badge>
                          )}
                        </div>
                        {template.description && (
                          <div style={{
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: 12,
                            color: 'var(--ion-color-medium)',
                            marginTop: 2,
                          }}>
                            {template.description}
                          </div>
                        )}
                      </div>
                      <Badge variant={
                        template.priority === 'urgent' ? 'danger' :
                        template.priority === 'high' ? 'warning' :
                        template.priority === 'normal' ? 'primary' : 'neutral'
                      } size="sm">
                        {template.priority}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Preview Banner for Scheduled Announcements */}
          {isScheduled && (
            <div style={{
              marginBottom: 16,
              padding: '12px 16px',
              background: 'var(--ion-color-warning-shade)',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
              <IonIcon icon={timeOutline} style={{ fontSize: 24, color: 'var(--ion-color-warning)' }} />
              <div style={{ flex: 1 }}>
                <div style={{
                  fontFamily: 'var(--kff-heading-font)',
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--ion-text-color)',
                }}>
                  Scheduled Announcement
                </div>
                <div style={{
                  fontFamily: 'var(--kff-data-font)',
                  fontSize: 12,
                  color: 'var(--ion-color-medium)',
                }}>
                  Will go live: {format(new Date(formData.starts_at), 'MMM d, yyyy h:mm a')}
                </div>
              </div>
              <Button
                variant="primary"
                fill="outline"
                size="small"
                iconStart={eyeOutline}
                onClick={() => setShowPreview(true)}
              >
                Preview
              </Button>
            </div>
          )}

          {/* Icon & Title row */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0 }}>
              <label style={{
                display: 'block',
                fontFamily: 'var(--kff-data-font)',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--ion-color-medium)',
                marginBottom: 8,
              }}>
                Icon
              </label>
              <ClickableIcon
                icon={formData.icon}
                size="lg"
                onClick={() => setShowIconPickerModal(true)}
                fallback="📢"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextInput
                label="Title *"
                value={formData.title}
                onChange={(val) => setFormData(prev => ({ ...prev, title: val }))}
                placeholder="Enter announcement title..."
                variant="default"
              />
            </div>
          </div>

          {/* Message */}
          <div style={{ marginTop: 16 }}>
            <TextInput
              label="Message"
              value={formData.message}
              onChange={(val) => setFormData(prev => ({ ...prev, message: val }))}
              placeholder="Optional details..."
              multiline
              rows={3}
              autoResize
              variant="default"
            />
          </div>

          {/* Priority & Font Size row */}
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontFamily: 'var(--kff-data-font)',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--ion-color-medium)',
                marginBottom: 8,
              }}>
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as AnnouncementPriority }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: '1px solid var(--ion-border-color)',
                  background: 'var(--ion-background-color)',
                  color: 'var(--ion-text-color)',
                  fontFamily: 'var(--kff-data-font)',
                  fontSize: 14,
                }}
              >
                {PRIORITY_OPTIONS.map(opt => (
                  <option key={opt.id} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontFamily: 'var(--kff-data-font)',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--ion-color-medium)',
                marginBottom: 8,
              }}>
                Font Size
              </label>
              <select
                value={formData.font_size}
                onChange={(e) => setFormData(prev => ({ ...prev, font_size: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: '1px solid var(--ion-border-color)',
                  background: 'var(--ion-background-color)',
                  color: 'var(--ion-text-color)',
                  fontFamily: 'var(--kff-data-font)',
                  fontSize: 14,
                }}
              >
                {FONT_SIZE_OPTIONS.map(opt => (
                  <option key={opt.id} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Custom Color */}
          <div style={{ marginTop: 16 }}>
            <ColorPicker
              label="Custom Color (optional)"
              value={formData.color || null}
              onChange={(color) => setFormData(prev => ({ ...prev, color }))}
              helperText="Override the priority-based color"
              showCustomInput
              columns={10}
              swatchSize={32}
            />
            {formData.color && (
              <Button
                variant="primary"
                fill="clear"
                size="small"
                onClick={() => setFormData(prev => ({ ...prev, color: '' }))}
                style={{ marginTop: 8 }}
              >
                Clear custom color
              </Button>
            )}
          </div>

          {/* Toggles row */}
          <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_sticky}
                onChange={(e) => setFormData(prev => ({ ...prev, is_sticky: e.target.checked }))}
              />
              <span style={{ fontFamily: 'var(--kff-data-font)', fontSize: 14 }}>
                <IonIcon icon={pin} style={{ marginRight: 4 }} />
                Pin to top
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.show_on_dashboard}
                onChange={(e) => setFormData(prev => ({ ...prev, show_on_dashboard: e.target.checked }))}
              />
              <span style={{ fontFamily: 'var(--kff-data-font)', fontSize: 14 }}>
                Show on dashboard
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.show_on_screensaver}
                onChange={(e) => setFormData(prev => ({ ...prev, show_on_screensaver: e.target.checked }))}
              />
              <span style={{ fontFamily: 'var(--kff-data-font)', fontSize: 14 }}>
                Show on screensaver
              </span>
            </label>
          </div>

          {/* Dates */}
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontFamily: 'var(--kff-data-font)',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--ion-color-medium)',
                marginBottom: 8,
              }}>
                Start Date/Time
              </label>
              <input
                type="datetime-local"
                value={formData.starts_at}
                onChange={(e) => setFormData(prev => ({ ...prev, starts_at: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: '1px solid var(--ion-border-color)',
                  background: 'var(--ion-background-color)',
                  color: 'var(--ion-text-color)',
                  fontFamily: 'var(--kff-data-font)',
                  fontSize: 14,
                }}
              />
              <div style={{
                fontFamily: 'var(--kff-data-font)',
                fontSize: 12,
                color: 'var(--ion-color-medium)',
                marginTop: 4,
              }}>
                When to start showing
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontFamily: 'var(--kff-data-font)',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--ion-color-medium)',
                marginBottom: 8,
              }}>
                Expiry Date/Time
              </label>
              <input
                type="datetime-local"
                value={formData.expires_at}
                onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: '1px solid var(--ion-border-color)',
                  background: 'var(--ion-background-color)',
                  color: 'var(--ion-text-color)',
                  fontFamily: 'var(--kff-data-font)',
                  fontSize: 14,
                }}
              />
              <div style={{
                fontFamily: 'var(--kff-data-font)',
                fontSize: 12,
                color: 'var(--ion-color-medium)',
                marginTop: 4,
              }}>
                Optional - leave blank for no expiry
              </div>
            </div>
          </div>

          {/* Save as Template button (when form has content) */}
          {formData.title.trim() && (
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--ion-border-color)' }}>
              <Button
                variant="primary"
                fill="outline"
                expand="block"
                iconStart={saveOutline}
                onClick={() => setShowSaveTemplateModal(true)}
              >
                Save as Template
              </Button>
            </div>
          )}

          {/* Delete button for editing */}
          {editingAnnouncement && (
            <div style={{ marginTop: formData.title.trim() ? 12 : 24, paddingTop: formData.title.trim() ? 0 : 16, borderTop: formData.title.trim() ? 'none' : '1px solid var(--ion-border-color)' }}>
              <Button
                variant="danger"
                fill="outline"
                expand="block"
                iconStart={trash}
                onClick={() => {
                  closeFormModal();
                  setDeleteAlert({ isOpen: true, id: editingAnnouncement.id });
                }}
              >
                Delete Announcement
              </Button>
            </div>
          )}
        </ModalSection>
      </Modal>

      {/* Icon Picker Modal - separate from form modal */}
      <IconPickerModal
        isOpen={showIconPickerModal}
        onClose={() => setShowIconPickerModal(false)}
        onSave={(icon) => {
          setFormData(prev => ({ ...prev, icon }));
          setShowIconPickerModal(false);
        }}
        currentIcon={formData.icon}
        title="Choose Announcement Icon"
        subtitle="Select an emoji or icon for your announcement"
        defaultTab="emoji"
      />

      {/* Delete Confirmation */}
      <IonAlert
        isOpen={deleteAlert.isOpen}
        onDidDismiss={() => setDeleteAlert({ isOpen: false, id: null })}
        header="Delete Announcement"
        message="Are you sure you want to delete this announcement? This cannot be undone."
        buttons={[
          { text: 'Cancel', role: 'cancel' },
          { text: 'Delete', role: 'destructive', handler: handleDelete },
        ]}
      />

      {/* Save as Template Modal */}
      <Modal
        isOpen={showSaveTemplateModal}
        onClose={() => {
          setShowSaveTemplateModal(false);
          setTemplateName('');
          setTemplateDescription('');
        }}
        size="sm"
        header={{ title: 'Save as Template' }}
        footer={{
          primaryAction: {
            label: savingTemplate ? 'Saving...' : 'Save Template',
            onClick: handleSaveAsTemplate,
            disabled: !templateName.trim() || savingTemplate,
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: () => {
              setShowSaveTemplateModal(false);
              setTemplateName('');
              setTemplateDescription('');
            },
          },
        }}
      >
        <ModalSection spacing="normal">
          <TextInput
            label="Template Name *"
            value={templateName}
            onChange={setTemplateName}
            placeholder="e.g., Weekly Meeting Reminder"
            variant="default"
          />
          <div style={{ marginTop: 16 }}>
            <TextInput
              label="Description"
              value={templateDescription}
              onChange={setTemplateDescription}
              placeholder="Optional description for this template..."
              multiline
              rows={2}
              variant="default"
            />
          </div>
          <div style={{
            marginTop: 16,
            padding: 12,
            background: 'var(--ion-color-step-50)',
            borderRadius: 8,
          }}>
            <div style={{
              fontFamily: 'var(--kff-data-font)',
              fontSize: 12,
              color: 'var(--ion-color-medium)',
              marginBottom: 8,
            }}>
              Template will save:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Badge variant="neutral" size="sm">{formData.icon.name} Icon</Badge>
              <Badge variant="neutral" size="sm">{formData.priority} priority</Badge>
              {formData.is_sticky && <Badge variant="primary" size="sm">Pinned</Badge>}
              {formData.show_on_dashboard && <Badge variant="neutral" size="sm">Dashboard</Badge>}
              {formData.show_on_screensaver && <Badge variant="neutral" size="sm">Screensaver</Badge>}
            </div>
          </div>
        </ModalSection>
      </Modal>

      {/* Preview Modal for Scheduled Announcements */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        size="md"
        header={{ title: 'Announcement Preview' }}
        footer={{
          primaryAction: {
            label: 'Close Preview',
            onClick: () => setShowPreview(false),
          },
        }}
      >
        <ModalSection spacing="normal">
          <div style={{
            padding: 16,
            background: formData.color || (
              formData.priority === 'urgent' ? 'var(--ion-color-danger-shade)' :
              formData.priority === 'high' ? 'var(--ion-color-warning-shade)' :
              formData.priority === 'low' ? 'var(--ion-color-step-100)' :
              'var(--ion-color-primary-shade)'
            ),
            borderRadius: 12,
            border: formData.priority === 'urgent' ? '2px solid var(--ion-color-danger)' : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <span style={{
                fontSize: formData.font_size === 'xlarge' ? 48 :
                         formData.font_size === 'large' ? 36 :
                         formData.font_size === 'small' ? 24 : 32,
              }}>
                {formData.icon.type === 'emoji' ? formData.icon.name : '📢'}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontFamily: 'var(--kff-heading-font)',
                  fontWeight: 700,
                  fontSize: formData.font_size === 'xlarge' ? 24 :
                           formData.font_size === 'large' ? 20 :
                           formData.font_size === 'small' ? 14 : 18,
                  color: 'var(--ion-text-color)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  {formData.title || 'Untitled Announcement'}
                  {formData.is_sticky && (
                    <IonIcon icon={pin} style={{ fontSize: 16, color: 'var(--ion-color-primary)' }} />
                  )}
                </div>
                {formData.message && (
                  <div style={{
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: formData.font_size === 'xlarge' ? 18 :
                             formData.font_size === 'large' ? 16 :
                             formData.font_size === 'small' ? 12 : 14,
                    color: 'var(--ion-color-medium)',
                    marginTop: 8,
                  }}>
                    {formData.message}
                  </div>
                )}
              </div>
            </div>
            {formData.priority === 'urgent' && (
              <div style={{
                marginTop: 12,
                padding: '8px 12px',
                background: 'var(--ion-color-danger)',
                color: 'white',
                borderRadius: 6,
                fontFamily: 'var(--kff-data-font)',
                fontSize: 12,
                fontWeight: 600,
                textAlign: 'center',
              }}>
                URGENT
              </div>
            )}
          </div>
          <div style={{
            marginTop: 16,
            padding: 12,
            background: 'var(--ion-color-step-50)',
            borderRadius: 8,
            fontFamily: 'var(--kff-data-font)',
            fontSize: 13,
            color: 'var(--ion-color-medium)',
          }}>
            <strong>Scheduled for:</strong> {formData.starts_at ? format(new Date(formData.starts_at), 'EEEE, MMMM d, yyyy h:mm a') : 'Now'}
            {formData.expires_at && (
              <div style={{ marginTop: 4 }}>
                <strong>Expires:</strong> {format(new Date(formData.expires_at), 'EEEE, MMMM d, yyyy h:mm a')}
              </div>
            )}
          </div>
        </ModalSection>
      </Modal>
    </PageShell>
  );
};

export default AnnouncementsPage;
